import {
  call,
  delay,
  fork,
  put,
  takeEvery,
  takeLatest
} from 'redux-saga/effects'

import {
  createSystemStatusMessage,
  deleteSystemStatusMessage,
  getFeatureStatusMessages,
  getSystemStatusMessages,
  updateSystemStatusMessage
} from '../apis/systemStatus'
import { decodeClientIdFromJwt, getAccessToken } from '../actions/tokenBridge'

import {
  CREATE_STATUS_MESSAGE,
  CreateStatusMessageAction,
  DELETE_STATUS_MESSAGE,
  DeleteStatusMessageAction,
  LOAD_BANNER_STATUS_MESSAGES,
  LOAD_STATUS_MESSAGES,
  UPDATE_STATUS_MESSAGE,
  UpdateStatusMessageAction,
  loadBannerStatusMessages,
  loadStatusMessages
} from '../actions/systemStatus'
import { addError } from '../actions/errors'
import { showNotification } from '../actions/ui'

import {
  setBannerStatusMessages,
  setStatusMessageSaving,
  setStatusMessages,
  setStatusMessagesLoadingState
} from '../reducers/systemStatus'
import ISystemStatusMessage from '../model/ISystemStatusMessage'
import { intl } from '../index'

export default function* watchSystemStatus() {
  yield takeLatest(LOAD_BANNER_STATUS_MESSAGES, fetchBannerMessages)
  yield takeLatest(LOAD_STATUS_MESSAGES, fetchAdminMessages)
  yield takeEvery(CREATE_STATUS_MESSAGE, createMessage)
  yield takeEvery(UPDATE_STATUS_MESSAGE, updateMessage)
  yield takeEvery(DELETE_STATUS_MESSAGE, deleteMessage)
}

// Banner feed. Failures are SILENT by design — a broken status service must
// never block the app or raise an error toast. On failure we simply keep the
// last known messages (or none).
export function* fetchBannerMessages() {
  try {
    // Use the per-feature endpoint (this feature's messages + global ones) so
    // any signed-in user with the read scope sees the banner, not just
    // BackOffice. The backend requires client_id === featureId, so we pass the
    // caller's own client id decoded from the access token.
    const token = getAccessToken()
    const featureId = token ? decodeClientIdFromJwt(token) : null
    if (!featureId) {
      return
    }
    const messages = (yield call(
      getFeatureStatusMessages,
      featureId,
      'NonExpired'
    )) as ISystemStatusMessage[]
    yield put(setBannerStatusMessages({ messages }))
  } catch {
    // swallow — see note above
  }
}

export function* fetchAdminMessages() {
  try {
    yield put(setStatusMessagesLoadingState({ loading: true }))
    const messages = (yield call(
      getSystemStatusMessages,
      'All'
    )) as ISystemStatusMessage[]
    yield put(setStatusMessages({ messages }))
  } catch (error) {
    yield put(addError(LOAD_STATUS_MESSAGES, error))
    yield put(setStatusMessagesLoadingState({ loading: false }))
  }
}

export function* createMessage(action: CreateStatusMessageAction) {
  yield* saveMessage(
    () => createSystemStatusMessage(action.input),
    'systemStatus.snackbar.created',
    CREATE_STATUS_MESSAGE
  )
}

export function* updateMessage(action: UpdateStatusMessageAction) {
  yield* saveMessage(
    () => updateSystemStatusMessage(action.id, action.input),
    'systemStatus.snackbar.saved',
    UPDATE_STATUS_MESSAGE
  )
}

function* saveMessage(
  request: () => Promise<unknown>,
  successMessageId: string,
  // The action that triggered this save, so a failure is attributed to the
  // right operation (create vs update) for reducers/telemetry/error handling.
  errorActionType: string
) {
  try {
    yield put(setStatusMessageSaving({ saving: true }))
    yield call(request)
    yield put(loadStatusMessages())
    yield put(loadBannerStatusMessages())
    yield fork(notifyBriefly, intl.formatMessage({ id: successMessageId }))
  } catch (error) {
    yield put(addError(errorActionType, error))
  } finally {
    yield put(setStatusMessageSaving({ saving: false }))
  }
}

// Success snackbar that auto-hides after 1s. Forked by callers so the delay
// doesn't hold up their finally blocks (saving reset / panel close).
function* notifyBriefly(message: string) {
  yield put(showNotification(message))
  yield delay(1000)
  yield put(showNotification(null))
}

export function* deleteMessage(action: DeleteStatusMessageAction) {
  try {
    yield call(deleteSystemStatusMessage, action.id)
    yield put(loadStatusMessages())
    yield put(loadBannerStatusMessages())
    yield fork(
      notifyBriefly,
      intl.formatMessage({ id: 'systemStatus.snackbar.deleted' })
    )
  } catch (error) {
    yield put(addError(DELETE_STATUS_MESSAGE, error))
  }
}
